import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from '@mui/material';
import { FileUploadMatchColumns } from 'features/co-pilot/co-pilot.types';
import { OptionType } from 'features/ui/base-components/pai-auto-complete';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiCheckbox from 'features/ui/base-components/pai-checkbox';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiSelect from 'features/ui/base-components/pai-select';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { FileColumnVariants } from './linkedin-profile-finder.types';

interface Props {
  open: boolean;
  variant: FileColumnVariants;
  columns: { text: string; type: string }[];
  hasTableHeading: boolean;

  handleColumnTypeChange(idx: number): (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleHasTableHeadingChange(event: React.ChangeEvent<HTMLInputElement>): void;
  handleClose(): void;
  handleAcceptColumns(): void;
}

const FileColumnDialog: React.FC<Props> = ({
  open,
  variant,
  columns,
  hasTableHeading,
  handleColumnTypeChange,
  handleHasTableHeadingChange,
  handleClose,
  handleAcceptColumns,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const manualCampaignContactUploadOptions: OptionType[] = [
    { value: FileUploadMatchColumns.EMPTY, label: t('doNotImport') },
    { value: FileUploadMatchColumns.FIRST_NAME, label: t('firstName') },
    { value: FileUploadMatchColumns.LAST_NAME, label: t('lastName') },
    { value: FileUploadMatchColumns.LINKEDIN_URL, label: t('linkedinUrl') },
    { value: FileUploadMatchColumns.COMPANY, label: t('company') },
    { value: FileUploadMatchColumns.EMAIL, label: t('email') },
    { value: FileUploadMatchColumns.POSITION, label: t('position') },
    { value: FileUploadMatchColumns.CUSTOM_FIELD, label: t('customField') },

    // Add phoneNumber here:
    { value: FileUploadMatchColumns.PHONE_NUMBER, label: t('phoneNumber') },
  ];

  const linkedinProfileFinderContactsUploadOptions: OptionType[] = [
    { value: FileUploadMatchColumns.EMPTY, label: t('doNotImport') },
    { value: FileUploadMatchColumns.FIRST_NAME, label: t('firstName') },
    { value: FileUploadMatchColumns.LAST_NAME, label: t('lastName') },
    { value: FileUploadMatchColumns.COMPANY, label: t('company') },
    { value: FileUploadMatchColumns.EMAIL, label: t('email') },
  ];

  const linkedinProfileFinderWebsiteVisitorsUploadOptions: OptionType[] = [
    { value: FileUploadMatchColumns.EMPTY, label: t('doNotImport') },
    { value: FileUploadMatchColumns.COMPANY, label: t('company') },
    { value: FileUploadMatchColumns.ROLE, label: t('role') },
    { value: FileUploadMatchColumns.COUNTRY, label: t('country') },
  ];

  const blacklistedCompaniesOptions: OptionType[] = [
    { value: FileUploadMatchColumns.EMPTY, label: t('doNotImport') },
    { value: FileUploadMatchColumns.COMPANY, label: t('company') },
  ];

  const options: OptionType[] =
    variant === FileColumnVariants.MANUAL_CAMPAIGN_CONTACT_UPLOAD
      ? manualCampaignContactUploadOptions
      : variant === FileColumnVariants.LINKEDIN_PROFILE_FINDER_CONTACTS_UPLOAD
      ? linkedinProfileFinderContactsUploadOptions
      : variant === FileColumnVariants.LINKEDIN_PROFILE_FINDER_WEBSITE_VISITORS_UPLOAD
      ? linkedinProfileFinderWebsiteVisitorsUploadOptions
      : variant === FileColumnVariants.BLACKLISTED_COMPANIES
      ? blacklistedCompaniesOptions
      : [];

  // Some logic to check required columns
  const nameSelected = columns.some(
    (col) =>
      col.type === FileUploadMatchColumns.FIRST_NAME || col.type === FileUploadMatchColumns.LAST_NAME || col.type === FileUploadMatchColumns.FULL_NAME
  );
  const urlSelected = columns.some((col) => col.type === FileUploadMatchColumns.LINKEDIN_URL);
  const companySelected = columns.some((col) => col.type === FileUploadMatchColumns.COMPANY);

  const firstNameSelected = columns.some((col) => col.type === FileUploadMatchColumns.FIRST_NAME);
  const lastNameSelected = columns.some((col) => col.type === FileUploadMatchColumns.LAST_NAME);
  const roleSelected = columns.some((col) => col.type === FileUploadMatchColumns.ROLE);
  const countrySelected = columns.some((col) => col.type === FileUploadMatchColumns.COUNTRY);
  const positionSelected = columns.some((col) => col.type === FileUploadMatchColumns.POSITION);

  // Decide if "Continue" or "OK" is disabled, depending on the variant
  const disabled =
    variant === FileColumnVariants.MANUAL_CAMPAIGN_CONTACT_UPLOAD
      ? (!nameSelected || (!urlSelected && !companySelected)) && (!positionSelected || !companySelected)
      : variant === FileColumnVariants.LINKEDIN_PROFILE_FINDER_CONTACTS_UPLOAD
      ? !firstNameSelected || !lastNameSelected || !companySelected
      : variant === FileColumnVariants.LINKEDIN_PROFILE_FINDER_WEBSITE_VISITORS_UPLOAD
      ? !companySelected || !roleSelected || !countrySelected
      : false;

  return (
    <Dialog open={open} classes={{ paper: classes.paper }} maxWidth={'sm'} fullWidth={true} transitionDuration={0}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>
          {variant === FileColumnVariants.BLACKLISTED_COMPANIES ? t('selectCompanyColumnTitle') : t('fileColumnDialogTitle')}
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={1}>
          <Typography className={classes.text}>
            {variant === FileColumnVariants.BLACKLISTED_COMPANIES ? t('selectCompanyColumnContent') : t('fileColumnDialogContent')}
          </Typography>
          <Typography className={classes.text}>
            {variant === FileColumnVariants.MANUAL_CAMPAIGN_CONTACT_UPLOAD
              ? t('fileColumnDialogCampaignContactContent')
              : variant === FileColumnVariants.LINKEDIN_PROFILE_FINDER_CONTACTS_UPLOAD
              ? t('fileColumnDialogProfileFinderContactsContent')
              : variant === FileColumnVariants.LINKEDIN_PROFILE_FINDER_WEBSITE_VISITORS_UPLOAD
              ? t('fileColumnDialogProfileFinderWebsiteVisitorsContent')
              : ''}
          </Typography>
          <div style={{ padding: '0 8px', marginBottom: '16px' }}>
            <PaiCheckbox checked={hasTableHeading} onChange={handleHasTableHeadingChange} label={t('fileHasTableHeading')} />
          </div>
          <Stack direction={'row'} justifyContent={'space-between'}>
            <Typography className={classes.tableHead}>{t('columnPreview')}</Typography>
            <Typography className={classes.tableHead} sx={{ width: '200px' }}>
              {variant === FileColumnVariants.BLACKLISTED_COMPANIES ? t('property') : t('contactProperty')}
            </Typography>
          </Stack>
          <Divider />
          {columns.map((col, index) => (
            <Stack key={index} gap={1}>
              <Stack direction={'row'} justifyContent={'space-between'}>
                <Typography className={classes.text}>{_.truncate(col.text, { length: 100 })}</Typography>
                <div>
                  <PaiSelect value={col.type} onChange={handleColumnTypeChange(index)} style={{ width: '200px' }}>
                    {options.map((option) => (
                      <PaiMenuItem
                        key={option.value}
                        value={option.value}
                        hidden={columns.some((col) => col.type === option.value && option.value !== '')}
                      >
                        {option.label}
                      </PaiMenuItem>
                    ))}
                  </PaiSelect>
                </div>
              </Stack>
              {index !== columns.length - 1 ? <Divider /> : null}
            </Stack>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'sm'} underline={'always'} action={handleClose} />
        <PaiButton
          text={variant === FileColumnVariants.MANUAL_CAMPAIGN_CONTACT_UPLOAD ? t('continue') : t('ok')}
          action={handleAcceptColumns}
          disabled={disabled}
        />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: 0,
  },
  title: {
    background: theme.palette.primary.main,
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '16px 8px 24px 8px !important',
    whiteSpace: 'pre-wrap',
  },
  tableHead: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
    padding: '0 8px',
  },
  text: {
    fontSize: theme.typography.pxToRem(13),
    padding: '0 8px',
    wordBreak: 'break-word',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default FileColumnDialog;
